import { Bar, BarChart, CartesianGrid, Cell, Text, Legend, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CustomTool } from './customTool';
import { formatNumber } from '../../../core/formatter/formatNumber';
import * as React from 'react';
import { SectionStyle } from '../../../helpers/SectionStyle';
import style from '../../../core/forms/style/WForm.less'

interface SimpleBarChartProps extends React.ClassAttributes<any> {
    data: any[];
    XKey: string;
    YKeys: string[];
    hideLegend?: boolean;
    customLegends?: any[];
    legendProps?: any;
    containerSize?: any;
    id?: string;
    selectable?: any;
    selectedChartKey?: string;
    tooltipType?: string;
    XAxisAngle?: number;
    XAxisHeight?: number;
    YAxisLabel?: any;
    YAxisUnit?: string;
    YAxisLine?: boolean;
    YAxisTickLine?: boolean;
    useTicksRender?: boolean;
    barSize?: number;
    fontSize?: string;
    hideLabelsFilter?: (data: any, rows: any[], index: number) => boolean;
    onSelectChange?: (key: string) => void;
    setTitle?: (elem?: JSX.Element) => JSX.Element;
    elemSelectPostition?: string;
}

interface SimpleBarChartState extends React.ClassAttributes<any> {
    selected: string;
}


export class SimpleBarChart extends React.Component<SimpleBarChartProps, SimpleBarChartState> {

    constructor(props: any) {
        super(props);
        this.state = {
            selected: typeof props.selectedChartKey === 'string' ? props.selectedChartKey : undefined
        };
        this.renderTicks = this.renderTicks.bind(this);
    }

    render(): JSX.Element {
        const { data, XKey, YKeys, containerSize, selectable, hideLegend, tooltipType, useTicksRender,
            XAxisAngle, XAxisHeight, YAxisLabel, YAxisUnit, YAxisLine, YAxisTickLine, fontSize, legendProps, setTitle, elemSelectPostition } = this.props;
        // console.log(data);
        const containerId: string = typeof SectionStyle.curPrintSectionId === 'number' ? SectionStyle.getCurrentClass(true) : null;
        const Section: any = new SectionStyle(containerId);
        const backgroundColorSectionChart: string = Section.getPropertyValue('--color-section-chart-background');
        const width = containerSize && containerSize.width ? containerSize.width : 600;
        const height = containerSize && containerSize.height ? containerSize.height : '100%';

        return <>
            {setTitle && setTitle(selectable && elemSelectPostition === 'title' && this.renderPieSelect())}
            <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                {selectable && elemSelectPostition === 'chart' && this.renderPieSelect()}
                <div style={{
                    width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
                    // Wenn sich die Legende auf der rechten Seite des Diagramms befindet.
                    ...(legendProps && legendProps.position === 'right' ? { flexDirection: 'row', alignItems: 'center' } : {})
                }}>
                    {Array.isArray(data) && data.length > 0 &&
                        <ResponsiveContainer width={width} height={height} id={this.props.id}>
                            <BarChart
                                style={{ fontSize: fontSize || '0.9em' }}
                                data={data}
                                layout='horizontal'
                                margin={{ top: 20, right: 20, bottom: 10, left: 0 }}
                            >
                                <XAxis
                                    dataKey={XKey}
                                    stroke='#555'
                                    tick={useTicksRender || XAxisAngle ? this.renderTicks : { fill: '#555' }}
                                    height={XAxisHeight}
                                    interval={0}
                                />
                                <YAxis
                                    type='number'
                                    stroke='#555'
                                    tick={{ fill: '#555' }}
                                    tickFormatter={(value: number) => formatNumber(value)}
                                    label={YAxisLabel}
                                    axisLine={YAxisLine}
                                    tickLine={YAxisTickLine}
                                    unit={YAxisUnit}
                                />
                                <Tooltip
                                    content={((contentData: any) =>
                                        <CustomTool
                                            data={contentData}
                                            dataKeys={YKeys}
                                            legendTitle={data.map((d: any) => d.legend)}
                                            source={tooltipType || 'payload'}
                                            fontSize={fontSize}
                                        />
                                    )}
                                    wrapperStyle={{
                                        color: '#888',
                                        backgroundColor: 'whitesmoke',
                                        border: '1px solid #ccc',
                                        padding: 5,
                                        opacity: 0.9
                                    }}
                                />
                                <CartesianGrid vertical={false} stroke='#bbb' fill={backgroundColorSectionChart} />
                                {this.renderBars()}
                            </BarChart>
                        </ResponsiveContainer>
                    }
                    {!hideLegend && this.renderLegend()}
                </div>
            </div>
        </>;
    }

    renderBars(): JSX.Element[] {
        const { data, XKey, YKeys, barSize } = this.props;
        return YKeys.map((yk: string, i: number) =>
            <Bar
                key={`bar-${i}`}
                dataKey={yk}
                isAnimationActive={false}
                barSize={barSize}
            >
                {data.map((d: any, idx: number) => {
                    // const opacity = Math.max(0.2, (10 - 2 * (YKey.length - 1 - i)) / 10);
                    const extVal = d.extraValues && d.extraValues[yk] ? d.extraValues[yk] : {};
                    const yKeyColor = Array.isArray(d.yKeyColors) && d.yKeyColors.length > 0 ? d.yKeyColors[i % d.yKeyColors.length] : undefined;
                    const color = yKeyColor || extVal.color || d.color;
                    return <Cell
                        key={`cell-${i}-${idx}`}
                        fill={color}
                    // opacity={opacity}
                    />;
                })}
                {/* <LabelList dataKey={yk} position="top" /> */}
            </Bar>
        );
    }

    renderPieSelect(): JSX.Element {
        const { selectable, onSelectChange, elemSelectPostition } = this.props;
        const { selected } = this.state;
        let divClass: string = null;
        let divStyle: any = {
            textAlign: 'right',
            marginRight: 10
        }
        if (elemSelectPostition === 'title') {
            divClass = style.formCompact;
            divStyle = null;
        }
        return (
            <div className={divClass} style={divStyle}>
                <select
                    style={{ backgroundColor: 'white' }}
                    value={selected}
                    onChange={(evt: any) => {
                        const key = evt.target.value;
                        this.setState({ selected: key });

                        if (onSelectChange) {
                            onSelectChange(key);
                        }
                    }}
                >
                    {Object.keys(selectable).map((k: string, idx: number) =>
                        <option key={`option-${idx}`} value={k}>{k}</option>
                    )}
                </select>
            </div>
        );
    }

    renderLegend(): JSX.Element {
        const { fontSize, legendProps } = this.props;
        const customLegends: any[] = this.props.customLegends || [];
        const style: any = (legendProps || {}).style || {};
        let data: any[] = this.props.data || [];
        if (data.length && customLegends.length) {
            data = [...customLegends, ...data];
        }
        return <div
            style={{ lineHeight: '1em', fontSize: fontSize || '0.9em', marginTop: -10, marginBottom: 10 }}
        >
            <div
                style={{
                    display: 'flex', flexDirection: 'row', padding: '0 20px 0 50px', flexWrap: 'wrap', justifyContent: 'center',
                    // Wenn sich die Legende auf der rechten Seite des Diagramms befindet.
                    ...(legendProps && legendProps.position === 'right' ? { padding: 0, marginLeft: -20, width: 100 } : {}),
                    ...style
                }}
            >
                {data.map((d: any, idx: number) =>
                    <div
                        key={`legend-${idx}`}
                        style={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline'/*, ...(idx && { marginLeft: 10 })*/ }}
                    >
                        <div style={{ width: 10, height: 10, backgroundColor: d.color, marginRight: 5, marginLeft: 10 }} />
                        <div>{d.legend}</div>
                    </div>
                )}
            </div>
        </div>;
    }

    renderTicks(props: any): JSX.Element {
        const { XAxisAngle } = this.props;
        const textAnchor = XAxisAngle ? 'end' : 'middle';
        const verticalAnchor = XAxisAngle ? 'end' : 'start';
        const transform = XAxisAngle ? `rotate(${XAxisAngle})` : undefined;
        const width = XAxisAngle ? undefined : props.width / props.visibleTicksCount;
        return (
            <g transform={`translate(${props.x},${props.y})`}>
                <Text
                    width={width}
                    x={0}
                    y={0}
                    dy={2}
                    textAnchor={textAnchor}
                    verticalAnchor={verticalAnchor}
                    fill='#666'
                    transform={transform}
                >
                    {props.payload.value}
                </Text>
            </g>
        );
    }
}
